import React, { useContext, useEffect, useRef, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import type { JSX } from 'react';
import type Order from '../../Models/OrderModel';
import type { ApiContextProps } from '../../Contexts/ApiContext';
import ApiContext from '../../Contexts/ApiContext';
import type { TokenContextProps } from '../../Contexts/TokenContext';
import TokenContext from '../../Contexts/TokenContext';
import { ErrorMessage, LoadingButton } from 'shouldve-been-mui';
import type { SystemContextProps } from '../../Contexts/SystemContext';
import SystemContext from '../../Contexts/SystemContext';
import type { UserContextProps } from '../../Contexts/UserContext';
import UserContext from '../../Contexts/UserContext';
import FormControl from '@mui/material/FormControl';
import Input from '@mui/material/Input';
import { handleTab } from 'shouldve-been-react';
import InputLabel from '@mui/material/InputLabel';
import type { SelectChangeEvent } from '@mui/material/Select';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';

type AddRemarkDialogProps = {
    order: Order,
    controller: boolean,
    onClose: (isAdded: boolean) => void,
};

function AddRemarkDialog(props: Readonly<AddRemarkDialogProps>): JSX.Element {

    const apiContext: ApiContextProps = useContext<ApiContextProps>(ApiContext);
    const tokenContext: TokenContextProps = useContext<TokenContextProps>(TokenContext);
    const systemContext: SystemContextProps = useContext<SystemContextProps>(SystemContext);
    const userContext: UserContextProps = useContext<UserContextProps>(UserContext);

    const inputRef: React.RefObject<HTMLInputElement> = useRef<HTMLInputElement>(null);

    const [remark, setRemark] = useState<string>('');
    const [visibleFor, setVisibleFor] = useState<string>('default');

    const [latestKeyEvent, setLatestKeyEvent] = useState<React.KeyboardEvent | null>(null);
    const [startPosition, setStartPosition] = useState<number | null>(null);

    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [pending, setPending] = useState<boolean>(false);

    const [windowSize, setWindowSize] = useState<[number, number]>([1920, 1080]);

    useEffect(() => {

        function updateSize(): void {
            setWindowSize([window.innerWidth, window.innerHeight]);
        }

        window.addEventListener('resize', updateSize);
        updateSize();
        return () => window.removeEventListener('resize', updateSize);
    }, []);

    useEffect(() => {

        if (latestKeyEvent !== null && startPosition !== null) {

            (latestKeyEvent.target as HTMLInputElement).selectionStart = startPosition + 1;
            (latestKeyEvent.target as HTMLInputElement).selectionEnd = startPosition + 1;
        }

    }, [remark, latestKeyEvent, startPosition]);

    async function handleClose(willAdd: boolean): Promise<void> {

        if (willAdd) {

            setPending(true);
            
            const success: boolean = await apiContext.api?.saveRemark(props.order.orderId, new Date(), userContext.currentUser?.signature ?? null, userContext.currentUser?.userId ?? null, remark, visibleFor === 'internal', visibleFor === 'includeToSupplier', systemContext.serverTimezoneOffsetMinutes ?? 0, tokenContext.token) ?? false;

            setPending(false);

            if (success) {
                props.onClose(true);
            } else {
                setErrorMessage('Failed to add remark');
            }
        } else {
            props.onClose(false);
        }
    }

    return (
        <Dialog 
            open={props.controller} 
            onClose={(): void => props.onClose(false)}
            TransitionProps={{
                onEntered: (): void => setErrorMessage(null),
            }}
        >
            <DialogTitle>Add Remark - {(props.order.vesselCode ?? '') + '_' + (props.order.orderRef ?? '')}</DialogTitle>
            <DialogContent>
                <FormControl
                    onClick={(): void => inputRef.current?.focus()}
                    style={{
                        height: '300px',
                        width: Math.min(windowSize[0] * 0.6, 500),
                        overflowX: 'auto',
                        border: '1px solid #777777',
                        borderRadius: '5px',
                        cursor: 'text',
                    }}
                >
                    <Input
                        type='text'
                        value={remark}
                        onChange={(e: React.ChangeEvent): void => setRemark((e.target as HTMLInputElement).value)}
                        onKeyDown={(e: React.KeyboardEvent): void => handleTab(e, remark, setRemark, setLatestKeyEvent, setStartPosition)}
                        multiline={true}
                        disableUnderline={true}
                        inputRef={inputRef}
                    />
                </FormControl>
                <br />
                <br />
                <FormControl 
                    size='small'
                    style={{
                        width: Math.min(windowSize[0] * 0.6, 500),
                    }}
                >
                    <InputLabel>Visible For</InputLabel>
                    <Select
                        value={visibleFor}
                        onChange={(e: SelectChangeEvent): void => setVisibleFor((e.target as HTMLInputElement).value)}
                        size='small'
                        variant='outlined'
                        label='Visible For'
                    >
                        <MenuItem
                            value='default'
                        >
                            Default
                        </MenuItem>
                        <MenuItem
                            value='internal'
                        >
                            Internal
                        </MenuItem>
                        <MenuItem
                            value='includeToSupplier'
                        >
                            Include To Supplier
                        </MenuItem>
                    </Select>
                </FormControl>
                <br />
                <ErrorMessage
                    message={errorMessage}
                    maxWidth={Math.min(windowSize[0] * 0.6, 300)}
                />
            </DialogContent>
            <DialogActions>
                <Button
                    onClick={async (): Promise<void> => handleClose(false)}
                    color='error'
                    variant='contained'
                    style={{
                        width: '90px',
                        height: '32px',
                        lineHeight: 'normal',
                        marginRight: '10px',
                    }}
                >
                    Close
                </Button>
                <LoadingButton 
                    width='90px'
                    height='32px'
                    text='Save'
                    controller={pending}
                    onClick={async (): Promise<void> => handleClose(true)}
                    buttonColor='primary'
                    circularColor='error'
                    circularSize='30px'
                    variant='contained'
                />
            </DialogActions>
        </Dialog>
    );
}

export default AddRemarkDialog;
